import type { TFunction } from "i18next";
import type { SegmentKind, TimelineBlock } from "../../api/types";
import { LABEL_MIN_PX } from "../geometry";
import { blockLines } from "../label";
import { theme } from "../theme";

/**
 * A face a caption line is written in: the font, and the vertical room the line takes -- not
 * the same number. They travel together because a stack that centres itself needs both, and a
 * font changed without its leading is a stack that overlaps.
 */
export interface Face {
  font: string;
  height: number;
}

const SANS = "ui-sans-serif, system-ui, sans-serif";

export const FACE = {
  /** A show's heading. The largest thing in the row, because a show contains the rest of it. */
  title: { font: `600 13px ${SANS}`, height: 15 },
  /** A track's heading: the same weight a size down, because a track sits inside a show. */
  trackTitle: { font: `600 11px ${SANS}`, height: 13 },
  /** Who played it. */
  sub: { font: `500 11px ${SANS}`, height: 13 },
  /** The state tag. */
  small: { font: `500 10px ${SANS}`, height: 12 },
} as const satisfies Record<string, Face>;

/**
 * What each lane writes a caption with: the face of its heading, and how far inside the block
 * the text sits.
 *
 * One table, because the caption is asked two things -- how to draw it, and what drawing it
 * costs in width -- and the second is a promise the first has to keep. A zoom that buys exactly
 * the measured width and then meets a wider inset lands a character short of the name it went
 * to find.
 */
export const CAPTION = {
  show: { face: FACE.title, insetPx: 7 },
  track: { face: FACE.trackTitle, insetPx: 5 },
} as const satisfies Record<SegmentKind, { face: Face; insetPx: number }>;

export interface CaptionLine extends Face {
  text: string;
  colour: string;
}

/** The two tiers of label colour a block wears: its heading, and everything under it. */
function ink(look: Look): { text: string; dim: string } {
  switch (look) {
    case "dead":
      return { text: theme.tombstoneText, dim: theme.tombstoneTextDim };
    case "live":
      return { text: theme.liveText, dim: theme.liveTextDim };
    case "provisional":
      return { text: theme.provisionalText, dim: theme.provisionalTextDim };
    case "track":
      return { text: theme.trackText, dim: theme.trackTextDim };
    default:
      return { text: theme.showText, dim: theme.showTextDim };
  }
}

/** The four states a show wears, and the one thing only the track lane is. */
export type ShowLook = "dead" | "live" | "provisional" | "plain";
/** The three looks a track wears, and so which of them may merge into one another. */
export type TrackLook = "dead" | "matched" | "plain";
type Look = ShowLook | "track";

/**
 * What a block writes on itself: its name, and who played it under that.
 *
 * Stacked rather than joined. "Djinn - Formless" is how the station announces a show, but on a
 * block it is a sentence that runs out of width and ellipsises away the half that identifies
 * it -- and the two parts are not equally important, which one line in one colour cannot say.
 *
 * Where the show came from is deliberately not here. "LIVE from Manchester" is the same clause
 * on every block of a night, so a row of them is a column of the same phrase repeated: it
 * separates nothing, and it costs the line that does. The tooltip still carries it.
 */
export function captionLines(
  item: TimelineBlock,
  look: Look,
  face: Face,
  fallback: string,
  t: TFunction,
): CaptionLine[] {
  const paint = ink(look);
  const { name, performer } = blockLines(item, t);
  const lines: CaptionLine[] = [{ text: name ?? fallback, colour: paint.text, ...face }];
  if (performer) lines.push({ text: performer, colour: paint.dim, ...FACE.sub });
  return lines;
}

/**
 * Write a caption, centred in the band as one group.
 *
 * As a group, not line by line: a row of blocks whose titles each sit at a different height
 * because they carry different numbers of lines is unreadable at a glance.
 */
function drawCaption(
  ctx: CanvasRenderingContext2D,
  lines: readonly CaptionLine[],
  x: number,
  middle: number,
  maxWidth: number,
): void {
  let y = middle - lines.reduce((total, line) => total + line.height, 0) / 2;
  for (const line of lines) {
    ctx.font = line.font;
    ctx.fillStyle = line.colour;
    ctx.fillText(ellipsise(ctx, line.text, maxWidth), x, y + line.height / 2);
    y += line.height;
  }
}

/**
 * The same caption, put inside a block: clipped to it, inset from its edges, centred in the
 * band. Both lanes and the ghost go through here, so a block cannot be written on in one lane
 * by rules the other does not share.
 *
 * The clip is a pixel looser than the text box on either side, so a glyph that overhangs its
 * own advance width is not shaved by the very rectangle drawn to keep the caption off the edge.
 */
export function writeCaption(
  ctx: CanvasRenderingContext2D,
  lines: readonly CaptionLine[],
  lane: SegmentKind,
  x0: number,
  width: number,
  top: number,
  height: number,
): void {
  const inset = CAPTION[lane].insetPx;
  ctx.save();
  ctx.beginPath();
  ctx.rect(x0 + inset - 1, top, width - 2 * (inset - 1), height);
  ctx.clip();
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  drawCaption(ctx, lines, x0 + inset, top + height / 2, width - 2 * inset);
  ctx.restore();
}

/**
 * How wide a block has to be drawn before it can say what it is, or null when it has nothing
 * to say.
 *
 * Not `LABEL_MIN_PX`, which is the width at which a caption is drawn at all: what gets drawn
 * there is "Djin…". This is the other number -- what reading the name in full costs -- because
 * that is what a click asking to see it has to buy. Measured rather than assumed: a title is
 * any length, and the two tiers are written in different faces.
 *
 * The state tag is not part of it. "UNVERIFIED" is wider than most titles and it is a flag
 * rather than a name, so zooming until it fits would answer a question nobody asked.
 */
export function nameSpanPx(
  ctx: CanvasRenderingContext2D,
  item: TimelineBlock,
  t: TFunction,
): number | null {
  // Nothing to reveal: the block draws "untitled", and no width turns that into a name.
  if (!blockLines(item, t).name) return null;
  const { face, insetPx } = CAPTION[item.kind];
  // The lines the renderer would write, in the faces it would write them in. Which ink they
  // land in is not a question here, so any look does.
  let widest = 0;
  for (const line of captionLines(item, "track", face, "", t)) {
    ctx.font = line.font;
    widest = Math.max(widest, ctx.measureText(line.text).width);
  }
  return Math.max(LABEL_MIN_PX, Math.ceil(widest) + 2 * insetPx);
}

/**
 * Trim to fit, with an ellipsis. Binary search rather than a character-at-a-time loop:
 * measureText is the expensive call here and a long title at a narrow width would otherwise
 * cost dozens of them per block per frame.
 */
export function ellipsise(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (maxWidth <= 0) return "";
  if (ctx.measureText(text).width <= maxWidth) return text;

  let low = 0;
  let high = text.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (ctx.measureText(`${text.slice(0, mid)}…`).width <= maxWidth) low = mid;
    else high = mid - 1;
  }
  return low > 0 ? `${text.slice(0, low)}…` : "";
}
